import {
  Color,
  Mesh,
  MeshStandardMaterial,
  PointLight,
  SphereGeometry,
  Sprite,
  SpriteMaterial,
  Vector3,
  AdditiveBlending,
} from 'three';
import { createTrail, clearTrail } from './trails.js';

export class CelestialBody {
  /**
   * @param {object} opts
   * @param {import('three').Scene} scene
   * @param {import('three').Texture} glowTexture
   */
  constructor(opts, scene, glowTexture) {
    this.scene = scene;
    this.id = opts.id;
    this.name = opts.name;
    this.mass = opts.mass;
    this.position = opts.position.clone();
    this.velocity = opts.velocity.clone();
    this.accel = new Vector3();
    this.color = opts.color;
    this.baseColor = new Color(opts.color);
    this.radius = opts.radius;
    this.isStar = opts.isStar;

    const geo = new SphereGeometry(this.radius, 32, 32);
    const mat = this.isStar
      ? new MeshStandardMaterial({
          color: this.color,
          emissive: this.color,
          emissiveIntensity: 0.72,
          metalness: 0.05,
          roughness: 0.4,
        })
      : new MeshStandardMaterial({
          color: this.color,
          metalness: 0.2,
          roughness: 0.55,
        });
    this.mesh = new Mesh(geo, mat);
    this.mesh.userData.bodyId = this.id;
    this.mesh.position.copy(this.position);
    scene.add(this.mesh);

    if (this.isStar) {
      this.light = new PointLight(this.color, 0.85 + this.mass * 0.6, 28 + this.mass * 8);
      this.light.position.copy(this.position);
      scene.add(this.light);
    }

    this.glow = new Sprite(
      new SpriteMaterial({
        map: glowTexture,
        color: this.color,
        transparent: true,
        opacity: this.isStar ? 0.32 : 0.16,
        blending: AdditiveBlending,
        depthWrite: false,
      }),
    );
    // compact corona — large scale + bloom was washing stars into one blob
    this.glow.scale.setScalar(this.isStar ? this.radius * 2.8 : this.radius * 2.2);
    this.glow.position.copy(this.position);
    scene.add(this.glow);

    createTrail(scene, this);
  }

  updateMesh() {
    this.mesh.position.copy(this.position);
    if (this.glow) this.glow.position.copy(this.position);
    if (this.light) this.light.position.copy(this.position);
  }

  dispose() {
    this.scene.remove(this.mesh);
    this.mesh.geometry.dispose();
    if (this.mesh.material) this.mesh.material.dispose();
    if (this.glow) {
      this.scene.remove(this.glow);
      this.glow.material.dispose();
    }
    if (this.light) this.scene.remove(this.light);
    if (this.trail) {
      clearTrail(this);
      this.scene.remove(this.trail);
      this.trail.geometry.dispose();
      this.trail.material.dispose();
      this.trail = null;
    }
  }
}
